import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  icpInterviewCostLabel,
  icpReadingEstimate,
  jevReadingMicroUsdOf,
  icpStaleItemIds,
  isJevReadItem,
  type IcpInterviewView,
  type IcpItem,
  type IcpItemRequest,
  type IcpRanked,
  type IcpRating,
  type IcpView,
  type JobDetail,
  type RankedBy,
  type RankingRow,
  type SaveIcpRequest,
} from '@nest-recruit/shared';
import { apiGet, errorMessage } from '../../../../lib/api-client';
import { useToasts } from '../../../../stores/toasts';
import { interviewKey, useIcpRatings, useInterviewActions, useLatestInterview, useRankIcp, useSaveIcp, type InterviewStep } from '../../../icp/api/icp-api';
import { baselineOverlay, isAtBaseline, itemOfInput, mergeDraftIntoForm } from '../../../icp/icp-form';
import { unreadItemIdsOf } from '../../icp-view';
import { useProfileRanks } from './use-profile-ranks';

// ADR 0061/0062 on RNK-01: everything the Ranking page needs from the ideal candidate profile in one place — the profile with the
// clicks laid over it, everyone's rank worked out in the browser from the stored readings (`icpRanksOf`, the same function the server
// ranks with), and the docked panel (interview or form) with the form's draft, which survives closing the panel so nothing typed is lost.

export type PanelMode = 'interview' | 'form';

/** The lines the form shows when it is not the saved profile: a draft from the interview, or the recruiter's unsaved edits. */
export interface FormDraft {
  readonly items: readonly IcpItem[];
  readonly interviewId: string | null;
  readonly notes: readonly string[];
  /** A new draft remounts the form, so its fields start from the new lines. */
  readonly key: number;
}

export interface IcpWorkspace {
  readonly isLoading: boolean;
  readonly error: boolean;
  readonly retry: () => void;
  readonly hasProfile: boolean;
  readonly version: number | null;
  /** The lines with the ratings on screen (the server's, with unconfirmed clicks laid over). */
  readonly items: readonly IcpItem[];
  readonly ratingsKey: string;
  /** The ratings of the profile's last save, by line id: "the job's rating" for the line filters (the Focus bar may have changed `items`). */
  readonly baselineRatings: Readonly<Record<string, IcpRating>>;
  readonly isAtBaseline: boolean;
  readonly staleIds: ReadonlySet<string>;
  /** Everyone's rank and per-line values, by application id; undefined without a profile. */
  readonly ranks: ReadonlyMap<string, IcpRanked> | undefined;
  /** The profile has lines Jev reads from the CV. */
  readonly readsCvs: boolean;
  /** ADR 0062: which engine writes this job's main ranking; 'criteria' while an existing job is still being moved. */
  readonly rankedBy: RankedBy;
  /** The list can be ordered by the profile now: it is the job's main ranking and a profile exists. */
  readonly canOrder: boolean;
  /** A Jev (or practice) provider can answer; false in production with no key. */
  readonly isJevConnected: boolean;
  /** The newest ask's words and parts, for the sheet's findings; null before any ask. */
  readonly currentAsk: IcpView['currentAsk'];
  /** The ask is reading CVs now (a full ask: Save and rank, Rank again, the move). */
  readonly reading: { readonly done: number; readonly total: number } | null;
  /** New CVs Jev is reading on its own, with the cost guess. */
  readonly readingNew: IcpView['readingNew'];
  /** The newest finished automatic read, "40 new CVs · 3.1¢". */
  readonly lastRead: IcpView['lastRead'];
  readonly spentMicroUsd: number;
  readonly autoPausedReason: string | null;
  /** What the move from the screening criteria left out or capped, shown once on the card. */
  readonly migrationNotes: readonly string[];
  /** What Ranking again would read and cost, "Ranking 96 CVs with Jev · about 6¢". */
  readonly rankEstimate: string;
  /** The same, in millionths of a dollar, over every line Jev reads (what the server checks a press against the job's Jev budget). */
  readonly rankMicroUsd: number;
  readonly askProblem: string | null;
  readonly flash: boolean;
  readonly isRanking: boolean;
  readonly rate: (itemId: string, rating: IcpRating) => void;
  readonly reset: () => void;
  readonly rank: () => void;
  // The docked panel
  readonly panel: PanelMode | null;
  readonly draft: FormDraft | null;
  readonly formItems: readonly IcpItem[];
  readonly interview: IcpInterviewView | null | undefined;
  readonly interviewStep: InterviewStep | null;
  readonly interviewCost: string | null;
  readonly estimate: (items: readonly IcpItemRequest[]) => string;
  /** What Save and rank with these lines is expected to cost, in millionths of a dollar, for the Jev budget's words. */
  readonly saveMicroUsd: (items: readonly IcpItemRequest[]) => number;
  readonly build: () => void;
  readonly edit: () => void;
  readonly close: () => void;
  readonly startInterview: () => void;
  readonly answer: (turn: number, answer: { readonly choices: readonly string[]; readonly text: string | null; readonly isSkipped: boolean }) => void;
  readonly nextQuestion: () => void;
  readonly draftNow: () => void;
  readonly editDraft: (items: readonly IcpItem[]) => void;
  readonly askMore: (items: readonly IcpItemRequest[]) => void;
  readonly save: (items: readonly IcpItemRequest[]) => void;
}

interface WorkspaceInput {
  readonly detail: JobDetail;
  readonly rows: readonly RankingRow[];
  /** The ranking's own word for which engine wrote the rows on screen (`RankingView.rankedBy`). */
  readonly rankedBy: RankedBy;
  /** The ranking reads this job's profile (it is a core part of Ranking: not behind the `jev` module). */
  readonly isOn: boolean;
  /** Sets (true) or clears (false) the address's Ideal candidate order. */
  readonly onOrder: (isOn: boolean) => void;
}

const NO_IDS: ReadonlySet<string> = new Set();
const NO_RATINGS: Readonly<Record<string, IcpRating>> = {};

export function useIcpWorkspace({ detail, rows, rankedBy, isOn, onOrder }: WorkspaceInput): IcpWorkspace {
  const queryClient = useQueryClient();
  const show = useToasts((toasts) => toasts.show);
  const jobId = detail.id;
  const ratings = useIcpRatings(jobId);
  const profileRanks = useProfileRanks({ jobId, job: detail, rows, rankedBy, isEnabled: isOn, overlay: ratings.overlay });
  const { view, items, ratingsKey, ranks, isJevConnected } = profileRanks;
  const profile = view?.profile ?? null;
  const readsCvs = items.some(isJevReadItem);
  const canOrder = profile !== null && rankedBy === 'profile';
  const newestAsk = view?.currentAsk ?? null;
  // The newest ask's parts say which lines are in the reading (a top-up has the same parts as the full ask).
  const staleIds = useMemo(() => {
    if (!profile) return NO_IDS;
    return rankedBy === 'profile' ? unreadItemIdsOf(items, rows) : new Set(icpStaleItemIds(items, newestAsk));
  }, [profile, rankedBy, items, rows, newestAsk]);

  const current = view?.currentAsk ?? null;
  // A top-up reads only new CVs and is shown as `readingNew`; this is the full ask (Save and rank, Rank again, the move).
  const reading = current?.status === 'running' && (current.icp?.topUpOf ?? null) === null ? { done: current.done, total: current.total } : null;
  const askProblem = current && current.status !== 'running' && current.problem ? current.problem : null;

  // ---- Power sorting ----
  const [flash, setFlash] = useState(false);
  const { rank, isRanking } = useRankIcp(jobId);
  const rate = (itemId: string, rating: IcpRating): void => {
    setFlash(true);
    ratings.setRating(itemId, rating);
  };
  const reset = (): void => {
    if (!profile) return;
    setFlash(false);
    ratings.reset(baselineOverlay(profile.items, profile.baselineRatings));
  };

  // ---- The docked panel ----
  const [panel, setPanel] = useState<PanelMode | null>(null);
  const [draft, setDraft] = useState<FormDraft | null>(null);
  const [isOpening, setOpening] = useState(false);
  const latest = useLatestInterview(jobId, panel === 'interview');
  const actions = useInterviewActions(jobId);
  const interview = latest.data;
  const formItems = draft?.items ?? items;
  const readableCvs = rows.filter((row) => row.cv === 'ready').length;

  const startFresh = async (mode: 'new' | 'more', lines: readonly IcpItemRequest[]): Promise<void> => {
    await actions.start({ mode, items: [...lines] });
  };

  /** "Build ideal candidate": an interview not yet drafted is picked up where it was left; otherwise a new one starts. */
  const build = (): void => {
    setPanel('interview');
    setOpening(true);
    void (async (): Promise<void> => {
      try {
        const last = await queryClient.fetchQuery<IcpInterviewView | null>({ queryKey: interviewKey(jobId), queryFn: () => apiGet<IcpInterviewView | null>(`/jobs/${jobId}/ranking/icp/interviews/latest`), staleTime: 0 });
        if (last?.status === 'asking' && last.mode === 'new') return;
        await startFresh('new', []);
      } catch (error) {
        show({ tone: 'danger', message: 'The interview didn’t start', detail: errorMessage(error) });
      } finally {
        setOpening(false);
      }
    })();
  };

  const askMore = (lines: readonly IcpItemRequest[]): void => {
    setDraft({ items: lines.map(itemOfInput), interviewId: draft?.interviewId ?? null, notes: [], key: draft?.key ?? 0 });
    setPanel('interview');
    void startFresh('more', lines);
  };

  const draftNow = (): void => {
    if (!interview) return;
    void (async (): Promise<void> => {
      const done = await actions.draft(interview.id);
      if (!done?.draft) return;
      const lines = done.mode === 'more' ? mergeDraftIntoForm(formItems, done.draft.items) : done.draft.items;
      setDraft({ items: lines, interviewId: done.id, notes: done.draft.notes, key: (draft?.key ?? 0) + 1 });
      setPanel('form');
    })();
  };

  // A refusal brings the form back with the words as they were (a 409 is someone else's save).
  const { save: saveProfile } = useSaveIcp(jobId, {
    onSaved: () => undefined,
    onFailed: (input) => {
      setDraft({ items: input.items.map(itemOfInput), interviewId: input.interviewId ?? null, notes: [], key: (draft?.key ?? 0) + 1 });
      setPanel('form');
      onOrder(false);
    },
  });

  const save = (lines: readonly IcpItemRequest[]): void => {
    const request: SaveIcpRequest = { version: profile?.version ?? null, items: [...lines], interviewId: draft?.interviewId ?? null, rank: true };
    ratings.discard();
    setPanel(null);
    setDraft(null);
    setFlash(false);
    onOrder(true);
    void saveProfile(request);
  };

  const interviewCost = interview ? icpInterviewCostLabel(interview.tokens) : null;

  return {
    isLoading: profileRanks.isLoading,
    error: profileRanks.isError,
    retry: profileRanks.refetch,
    hasProfile: profile !== null,
    version: profile?.version ?? null,
    items,
    ratingsKey,
    baselineRatings: profile?.baselineRatings ?? NO_RATINGS,
    isAtBaseline: profile ? isAtBaseline(items, profile.baselineRatings) : true,
    staleIds,
    ranks,
    readsCvs,
    rankedBy,
    canOrder,
    isJevConnected,
    currentAsk: newestAsk,
    reading,
    readingNew: view?.readingNew ?? null,
    lastRead: view?.lastRead ?? null,
    spentMicroUsd: view?.spentMicroUsd ?? 0,
    autoPausedReason: view?.autoPausedReason ?? null,
    migrationNotes: view?.migrationNotes ?? [],
    rankEstimate: icpReadingEstimate(items, newestAsk, readableCvs).label,
    rankMicroUsd: jevReadingMicroUsdOf(readableCvs, items.filter((item) => item.kind === 'jev').length),
    askProblem,
    flash,
    isRanking,
    rate,
    reset,
    rank: () => void rank(),
    panel,
    draft,
    formItems,
    interview,
    interviewStep: isOpening ? 'start' : actions.step,
    interviewCost,
    estimate: (lines) => icpReadingEstimate(lines.map(itemOfInput), newestAsk, readableCvs).label,
    saveMicroUsd: (lines) => jevReadingMicroUsdOf(readableCvs, lines.filter((line) => line.kind === 'jev').length),
    build,
    edit: () => setPanel('form'),
    close: () => setPanel(null),
    startInterview: () => void startFresh('new', []),
    answer: (turn, answer) => {
      if (interview) void actions.answer(interview, { turn, choices: [...answer.choices], text: answer.text, isSkipped: answer.isSkipped });
    },
    nextQuestion: () => {
      if (interview) void actions.next(interview.id);
    },
    draftNow,
    editDraft: (lines) => setDraft((previous) => ({ items: lines, interviewId: previous?.interviewId ?? null, notes: previous?.notes ?? [], key: previous?.key ?? 0 })),
    askMore,
    save,
  };
}
